export type TApiMethod = 'GET' | 'POST';

export type TApiField = {
  name: string;
  type?: string;
  required?: boolean;
  description: string;
};

export type TApiResponse = {
  status: string;
  body: string;
  description: string;
};

export type TApiEndpoint = {
  id: string;
  method: TApiMethod;
  path: string;
  summary: string;
  description?: string;
  mutates?: boolean;
  pathParams: TApiField[];
  queryParams?: TApiField[];
  bodyFields?: TApiField[];
  bodyExample?: string;
  responses: TApiResponse[];
};

export const API_BASE_PATH = '/api/public/v1';

const OID: TApiField = {
  name: 'oid',
  type: 'string',
  required: true,
  description: 'Organization ID the API key belongs to.',
};
const JOB_ID: TApiField = {
  name: 'jobId',
  type: 'string',
  required: true,
  description: 'Job ID; must belong to the organization.',
};

export const ENDPOINTS: TApiEndpoint[] = [
  {
    id: 'create-job',
    method: 'POST',
    path: `${API_BASE_PATH}/{oid}/{wsid}/job`,
    summary: 'Create a new job',
    mutates: true,
    pathParams: [
      OID,
      { name: 'wsid', type: 'string', required: true, description: 'Workspace ID the job is created in.' },
    ],
    bodyFields: [
      { name: 'notes', type: 'string', required: true, description: 'Free-text notes for the job.' },
      { name: 'attachments', type: 'string[]', required: true, description: 'File names attached to the job.' },
      { name: 'name', type: 'string', description: 'Display name of the job.' },
      { name: 'status', type: 'string', description: 'Initial job status.' },
      { name: 'deadline', type: 'string (ISO date)', description: 'Due date for the job.' },
      { name: 'tags', type: 'string[]', description: 'Tags shown on the job.' },
      { name: 'multichannel', type: 'boolean', description: 'Whether the audio should be treated as multichannel.' },
      {
        name: '…',
        description:
          'Accepts the remaining optional job fields from the job DTO, such as meeting metadata, assignee fields, and layoutId.',
      },
    ],
    bodyExample: JSON.stringify({ name: 'My job', notes: '', attachments: [] }, null, 2),
    responses: [{ status: '201', body: 'empty', description: 'Job created.' }],
  },
  {
    id: 'list-workspaces',
    method: 'GET',
    path: `${API_BASE_PATH}/{oid}/workspaces`,
    summary: 'List workspaces in the organization',
    pathParams: [OID],
    responses: [{ status: '200', body: 'JSON object', description: 'Organization ID and its workspaces.' }],
  },
  {
    id: 'get-job',
    method: 'GET',
    path: `${API_BASE_PATH}/{oid}/{jobId}`,
    summary: 'Get job details',
    pathParams: [OID, JOB_ID],
    responses: [{ status: '200', body: 'JSON object', description: 'The job with its current state.' }],
  },
  {
    id: 'resubmit',
    method: 'POST',
    path: `${API_BASE_PATH}/{oid}/{jobId}/resubmit`,
    summary: 'Resubmit a job through the pipeline',
    description: 'Equivalent to the admin Resubmit button. Re-uses the existing media and does not re-upload.',
    mutates: true,
    pathParams: [OID, JOB_ID],
    responses: [{ status: '201', body: 'JSON object', description: 'Resubmission result.' }],
  },
  {
    id: 'upload-params',
    method: 'GET',
    path: `${API_BASE_PATH}/{oid}/{jobId}/upload-params`,
    summary: 'Get S3 upload params for job files',
    pathParams: [OID, JOB_ID],
    queryParams: [
      { name: 'file', type: 'string', required: true, description: 'Name of the file that will be uploaded.' },
    ],
    responses: [
      {
        status: '200',
        body: 'JSON object',
        description: 'Presigned S3 POST target: url plus the form fields to include in the upload.',
      },
    ],
  },
  {
    id: 'attach-file',
    method: 'POST',
    path: `${API_BASE_PATH}/{oid}/{jobId}/file`,
    summary: 'Attach an S3-uploaded file to a job',
    mutates: true,
    pathParams: [OID, JOB_ID],
    bodyFields: [
      { name: 'fileName', type: 'string', required: true, description: 'Name of the uploaded file.' },
      { name: 'fileSize', type: 'string', required: true, description: 'Human-readable file size.' },
      {
        name: 'weight',
        type: 'number',
        required: true,
        description: '0-based position in the intended processing order (e.g. matters for concatenation jobs).',
      },
    ],
    bodyExample: JSON.stringify({ fileName: '', fileSize: '', weight: 0 }, null, 2),
    responses: [{ status: '201', body: 'string', description: 'ID of the attached media record.' }],
  },
  {
    id: 'submit',
    method: 'POST',
    path: `${API_BASE_PATH}/{oid}/{jobId}/submit`,
    summary: 'Submit a job',
    description: 'Submits the job into the processing pipeline once its files are attached.',
    mutates: true,
    pathParams: [OID, JOB_ID],
    responses: [{ status: '201', body: 'boolean', description: 'Whether the job was submitted.' }],
  },
];
